import { iso4217Exponent } from './money';

/**
 * The cheapest whole-cent AUD amount that is not below `floorAud`: the floor
 * itself when it is already a whole cent, otherwise the next cent up.
 *
 * A seller's floor is not always a whole cent. The product editor converts a
 * floor typed in another currency to AUD and saves it as it lands (A$7.5414),
 * but everything a buyer can pay is a whole cent: the product page rounds the
 * typed amount to the cent before it reaches the cart, and the server refuses a
 * pwywPrice under the RAW floor (resolveItemPriceAud). So this is the lowest
 * amount that can actually be paid, and a whole-cent amount clears it exactly
 * when it clears the raw floor, which is why it is safe to show and compare
 * against instead.
 *
 * Settled with the same comparison the server makes rather than a bare
 * ceiling, because `floorAud * 100` can land a hair either side of a whole
 * number: 1.1 * 100 is 110.00000000000001, which a ceiling turns into A$1.11,
 * and the next double above 3.51 multiplies out to exactly 351, which a ceiling
 * leaves at A$3.51 although the server refuses A$3.51 against it.
 *
 * @param floorAud The lowest amount the product accepts, in AUD.
 */
export function wholeCentFloorAud(floorAud: number): number {
  if (!Number.isFinite(floorAud)) return floorAud;
  let cents = Math.ceil(floorAud * 100);
  while ((cents - 1) / 100 >= floorAud) cents -= 1;
  while (cents / 100 < floorAud) cents += 1;
  return cents / 100;
}

/**
 * The amount a pay-what-you-want field starts at, as the string the buyer sees
 * in it: the seller's floor, stated in the currency the buyer TYPES in.
 *
 * The product page reads that field as display currency and converts it to AUD
 * once (toBaseAmount, then rounded to the cent) before comparing it with the
 * floor and adding it to the cart. Seeding it with the raw AUD floor therefore
 * quoted a buyer paying in yen a starting amount of a few yen, flagged as below
 * the minimum, and quoted a buyer paying in US dollars an amount roughly half as
 * large again as the floor.
 *
 * ROUNDED UP to the currency's smallest unit, never to nearest. At a rate of
 * 0.65098 a floor of A$5 is 3.2549, which rounds to nearest as 3.25, and 3.25
 * converts back to A$4.99: the field would open on its own error, "Amount is
 * below the minimum". Zero-decimal currencies (JPY, KRW and the rest of ISO
 * 4217's list in money.ts) round to a whole unit and are written with no
 * decimals, which is how the page's formatter shows them too.
 *
 * The floor is rounded UP to a whole cent (wholeCentFloorAud) BEFORE it is
 * converted. The seed converts back to no less than the AUD amount it came
 * from (float noise aside), and the page then rounds that to the nearest cent.
 * From a whole-cent floor that lands on or above it. From a raw one it could
 * land a cent under: A$7.5414 at 0.651 seeded 4.91, which converts back to
 * A$7.54224 and rounds to A$7.54, flagged by the page and refused by the server.
 *
 * The 1e-6 tolerance is float noise, not a discount: 7.65 can arrive as
 * 7.6500000000001, which a bare ceiling would turn into 7.66. The shortfall it
 * can admit is a millionth of the smallest unit, far inside the half cent the
 * conversion back to AUD rounds away.
 *
 * @param floorAud The lowest amount the product accepts, in AUD (whole cent or not).
 * @param toDisplayAmount AUD to the buyer's display currency (theme context).
 * @param displayCurrency The ISO code that conversion lands in.
 */
export function pwywSeedAmount(
  floorAud: number,
  toDisplayAmount: (aud: number) => number,
  displayCurrency: string,
): string {
  const exponent = iso4217Exponent(displayCurrency);
  const scale = 10 ** exponent;
  const units = Math.ceil(toDisplayAmount(wholeCentFloorAud(floorAud)) * scale - 1e-6);
  return (units / scale).toFixed(exponent);
}

/**
 * The AUD amount to hand formatCurrency for the "Minimum" labels, so they read
 * exactly what the field is seeded with.
 *
 * formatCurrency converts AUD to the display currency and rounds to NEAREST,
 * while the seed rounds UP. Formatting the whole-cent floor directly could
 * therefore print a minimum one unit under the seed (4.9149 shows as 4.91 while
 * the field opens on 4.92), and 4.91 typed back converts under the floor and is
 * refused. Converting the seed back to AUD makes formatCurrency land on the
 * seed itself. For an AUD viewer the seed IS the whole-cent floor.
 *
 * @param floorAud The lowest amount the product accepts, in AUD (whole cent or not).
 * @param toDisplayAmount AUD to the buyer's display currency (theme context).
 * @param toBaseAmount The display currency back to AUD (theme context).
 * @param displayCurrency The ISO code the conversions use.
 */
export function pwywMinimumLabelAud(
  floorAud: number,
  toDisplayAmount: (aud: number) => number,
  toBaseAmount: (display: number) => number,
  displayCurrency: string,
): number {
  const seed = Number(pwywSeedAmount(floorAud, toDisplayAmount, displayCurrency));
  return Number.isFinite(seed) && seed > 0 ? toBaseAmount(seed) : wholeCentFloorAud(floorAud);
}
